"use client"

import Link from "next/link";
import {TextareaField} from "@/components/form/TextareaField";
import {SubmitButton} from "@/components/form/SubmitButton";
import {Form} from "@/components/form/Form";
import {contactSchema} from "@/app/(home)/contactSchema";
import {TextField} from "@/components/form/TextField";
import {isSuccessResult} from "@/components/form/utils";
import {toast} from "sonner";
import {z} from "zod/v4";
import {contact} from "@/app/(home)/actions/contact";


export default function Footer() {
  async function onSubmit(data: z.infer<typeof contactSchema>) {
    const result = await contact(data)
    if (isSuccessResult(result)) {
      toast.success("Message sent successfully. We will get back to you shortly.")
    } else {
      toast.error(result.error.message)
    }
    return result
  }

  const year = new Date().getFullYear();

  return (
    <footer className="mt-auto border-t border-zinc-200 bg-zinc-50 text-zinc-700">
      <div className="mx-auto max-w-6xl px-6 py-12 grid grid-cols-1 gap-10 md:grid-cols-12">
        {/* Brand / mission */}
        <div className="md:col-span-4">
          <h3 className="text-xl font-semibold text-zinc-900">Freewill</h3>
          <p className="mt-3 text-sm leading-relaxed text-zinc-600">
            We empower global contribution through shared prosperity. Knowledge, transparency, and fair distribution to
            uplift everyone.
          </p>
        </div>

        {/* Navigation */}
        <nav className="md:col-span-2">
          <h4 className="text-sm font-medium text-zinc-900">Navigation</h4>
          <ul className="mt-3 space-y-2 text-sm">
            <li><Link className="hover:text-cyan-600" href="/">Home</Link></li>
            <li><Link className="hover:text-cyan-600" href="/marketplace">Marketplace</Link></li>
            <li><Link className="hover:text-cyan-600" href="/dashboard">Dashboard</Link></li>
          </ul>
        </nav>

        {/* Legal */}
        <nav className="md:col-span-2">
          <h4 className="text-sm font-medium text-zinc-900">Legal</h4>
          <ul className="mt-3 space-y-2 text-sm">
            <li><Link className="hover:text-cyan-600" href="/terms">Terms & Conditions</Link></li>
            <li><Link className="hover:text-cyan-600" href="/privacy">Privacy Policy</Link></li>
          </ul>
        </nav>

        {/* Contact */}
        <div className="md:col-span-4">
          <h4 className="text-sm font-medium text-zinc-900">Contact</h4>

          <Form schema={contactSchema} onSubmit={onSubmit} submitText={false} className="mt-3 space-y-3"
                aria-describedby="contact-status">
            <TextField label={"Name"} name="name"
                       className="mt-1 w-full rounded-md border border-zinc-300 bg-white text-zinc-900 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-cyan-500"/>

            <TextField label={"Email"} name="email"
                       className="mt-1 w-full rounded-md border border-zinc-300 bg-white text-zinc-900 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-cyan-500"/>
            <TextareaField label={"Message"} name="message"
                           className="mt-1 w-full rounded-md border border-zinc-300 bg-white text-zinc-900 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-cyan-500"/>
            <SubmitButton
              className="inline-flex items-center justify-center rounded-md bg-cyan-600 px-4 py-2 text-sm font-medium text-white hover:bg-cyan-700 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              Submit
            </SubmitButton>
          </Form>
        </div>
      </div>

      <div className="border-t border-zinc-200">
        <div
          className="mx-auto max-w-6xl px-6 py-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between text-xs text-zinc-500">
          <p>© {year} Freewill. All rights reserved.</p>
          <div className="flex items-center gap-4">
            <Link className="hover:text-cyan-600" href="/terms">Terms</Link>
            <Link className="hover:text-cyan-600" href="/privacy">Privacy</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
